import { Component, Input, Output, EventEmitter, inject, OnInit, OnDestroy, ChangeDetectionStrategy, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimeEntry } from '../../../../../shared/models/activity.model';
import { TimeEntriesService } from '../../../../../shared/services/time-entries.service';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../../../../../shared/services/project.service';
import { Project, ProjectStage } from '../../../../../shared/models/project.model';
import { TaskService } from '../../../../../shared/services/task.service';
import { Task } from '../../../../../shared/models/task.model';
import { FileService } from '../../../../../shared/services/file-s3.service';
import { ClientService, Todo } from '../../../../../shared/services/client.service';
import { UserService } from '../../../../../shared/services/user.service';
import { User as UserAuthService } from '../../../../../shared/services/user';
import { Subscription } from 'rxjs';

// MINI MODAL DRAGGABLE TO SHOW TIME ENTRY DETAIL
@Component({
  selector: 'app-mini-modal-detail-entry',
  imports: [CommonModule, FormsModule],
  templateUrl: './mini-modal-detail-entry.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniModalDetailTimeEntryComponent implements OnInit, OnDestroy {
  @Input() entry!: TimeEntry;
  @Input() position!: { top: number; left: number };
  @Output() close = new EventEmitter<void>();
  @Output() update = new EventEmitter<void>();

  private timeEntryService = inject(TimeEntriesService);
  private projectService = inject(ProjectService);
  private taskService = inject(TaskService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  fileService = inject(FileService);
  private clientService = inject(ClientService);
  private userService = inject(UserService);
  /** True when the entry is approved — no one can edit/delete in activity view. */
  get isLocked(): boolean {
    return (this.entry?.approvalStatus ?? 'DRAFT') === 'APPROVED';
  }

  projects: Project[] = [];
  allStages: ProjectStage[] = [];
  filteredStages: ProjectStage[] = [];
  allTasks: Task[] = [];
  filteredTasks: Task[] = [];

  selectedProjectId: string = '';
  selectedStageId: string = '';
  selectedTaskId: string = '';

  imageUrls: { path: string; url: string }[] = [];
  selectedFiles: File[] = [];

  imagesLoaded = false;
  isCrmTask = false;
  crmTaskLabel = '';
  showCrmPicker = false;     // toggle to show/hide the task picker
  crmTasks: Todo[] = [];     // all assigned CRM tasks
  filteredCrmTasks: Todo[] = [];
  crmPickerSearch = '';

  // Custom time picker options
  readonly hours = Array.from({ length: 21 }, (_, i) => i); // 0‑20
  readonly minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  getHour(date: Date | undefined): number {
    return date ? new Date(date).getHours() : 0;
  }
  getMinute(date: Date | undefined): number {
    const m = date ? new Date(date).getMinutes() : 0;
    // Round to nearest 5
    return this.minutes.reduce((prev, curr) =>
      Math.abs(curr - m) < Math.abs(prev - m) ? curr : prev, 0);
  }
  pad2(n: number): string { return String(n).padStart(2, '0'); }

  updateTimeFromSelects(field: 'start' | 'end', h: number, m: number) {
    this.updateTimeEntry({ [field === 'start' ? 'startTime' : 'endTime']: `${this.pad2(h)}:${this.pad2(m)}` } as any);
  }

  dragging = false;
  dragStartX = 0;
  dragStartY = 0;

  // Local position tracking for proper change detection
  currentTop = 0;
  currentLeft = 0;

  private projectsSub: Subscription | null = null;
  private stagesSub: Subscription | null = null;
  private tasksSub: Subscription | null = null;

  ngOnInit() {
    console.log('🟢 [DETAIL-MODAL] Opening for entry:', this.entry);
    console.log('🟢 [DETAIL-MODAL] Entry projectName:', this.entry?.projectName);

    // Initialize local position from input
    this.currentTop = this.position.top;
    this.currentLeft = this.position.left;

    // Detect CRM task entries (saved as "[Task] name")
    if (this.entry?.projectName?.startsWith('[Task] ')) {
      this.isCrmTask = true;
      this.crmTaskLabel = this.entry.projectName.replace(/^\[Task\] /, '');
      this.loadCrmTasks(); // pre-load so picker is ready instantly
      return; // skip project parsing for CRM task entries
    }

    // Parse current entry to extract project, stage, and task
    this.parseEntryProjectName();

    // S'abonner aux projets
    this.projectsSub = this.projectService.projects$.subscribe(projects => {
      console.log('🟢 [DETAIL-MODAL] Received', projects.length, 'projects');
      this.projects = projects;

      // Vérifier si le projet de l'entry existe dans la liste
      if (this.entry?.projectName) {
        const found = projects.find(p => p.projectName === this.entry.projectName);
        console.log('🟢 [DETAIL-MODAL] Project found in list:', !!found);
      }

      this.cdr.markForCheck();
    });

    // S'abonner aux stages
    this.stagesSub = this.projectService.projectsStage$.subscribe(stages => {
      this.allStages = stages;
      this.filterStages();
      this.cdr.markForCheck();
    });

    // S'abonner aux tasks
    this.tasksSub = this.taskService.tasks$.subscribe(tasks => {
      this.allTasks = tasks;
      this.filterTasks();
      this.cdr.markForCheck();
    });

    // S'assurer que les projets sont chargés
    if (this.projects.length === 0) {
      console.log('🟡 [DETAIL-MODAL] No projects loaded, triggering load...');
      this.projectService.listProjects();
    }
  }

  ngOnDestroy() {
    // Clean up event listeners on destroy
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    this.projectsSub?.unsubscribe();
    this.stagesSub?.unsubscribe();
    this.tasksSub?.unsubscribe();
  }

  isProjectInList(projectName: string): boolean {
    // Le projectName peut être "Project - Stage - Task", on extrait juste le nom du projet
    const baseProjectName = this.getBaseProjectName(projectName);
    return this.projects.some(p => p.projectName === baseProjectName);
  }

  // Extraire le nom du projet de base (avant " - ")
  getBaseProjectName(fullName: string): string {
    if (!fullName) return '';
    const parts = fullName.split(' - ');
    return parts[0].trim();
  }

  // Parser le projectName pour extraire project, stage, et task
  parseEntryProjectName() {
    if (!this.entry?.projectName) return;

    const parts = this.entry.projectName.split(' - ').map(p => p.trim());

    if (parts.length >= 1) {
      // Trouver le projet par nom
      const project = this.projects.find(p => p.projectName === parts[0]);
      if (project?.projectId) {
        this.selectedProjectId = project.projectId;
      }
    }

    if (parts.length >= 2) {
      // Trouver la stage par nom
      const stage = this.allStages.find(s => s.stageName === parts[1]);
      if (stage?.projectStageId) {
        this.selectedStageId = stage.projectStageId;
      }
    }

    if (parts.length >= 3) {
      // Trouver la task par nom
      const task = this.allTasks.find(t => t.nameTask === parts[2]);
      if (task?.id) {
        this.selectedTaskId = task.id;
      }
    }
  }

  // Filtrer les stages selon le projet sélectionné
  filterStages() {
    if (!this.selectedProjectId) {
      this.filteredStages = [];
      return;
    }
    this.filteredStages = this.allStages.filter(s => s.projectId === this.selectedProjectId);
  }

  // Filtrer les tasks selon la stage sélectionnée
  filterTasks() {
    if (!this.selectedStageId) {
      this.filteredTasks = [];
      return;
    }
    this.filteredTasks = this.allTasks.filter(t => t.projectStageId === this.selectedStageId);
  }

  // Appelé quand l'utilisateur change le projet
  onProjectChange(projectId: string) {
    this.selectedProjectId = projectId;
    this.selectedStageId = '';
    this.selectedTaskId = '';
    this.filterStages();
    this.filteredTasks = [];
    this.updateEntryProjectName();
  }

  // Appelé quand l'utilisateur change la stage
  onStageChange(stageId: string) {
    this.selectedStageId = stageId;
    this.selectedTaskId = '';
    this.filterTasks();
    this.updateEntryProjectName();
  }

  // Appelé quand l'utilisateur change la task
  onTaskChange(taskId: string) {
    this.selectedTaskId = taskId;
    this.updateEntryProjectName();
  }

  // Mettre à jour le projectName de l'entry selon les sélections
  updateEntryProjectName() {
    const parts: string[] = [];

    // Ajouter le nom du projet
    if (this.selectedProjectId) {
      const project = this.projects.find(p => p.projectId === this.selectedProjectId);
      if (project) {
        parts.push(project.projectName);
      }
    }

    // Ajouter le nom de la stage
    if (this.selectedStageId) {
      const stage = this.allStages.find(s => s.projectStageId === this.selectedStageId);
      if (stage) {
        parts.push(stage.stageName);
      }
    }

    // Ajouter le nom de la task
    if (this.selectedTaskId) {
      const task = this.allTasks.find(t => t.id === this.selectedTaskId);
      if (task) {
        parts.push(task.nameTask);
      }
    }

    const newProjectName = parts.join(' - ');
    this.entry.projectName = newProjectName;
    this.updateTimeEntry({
      projectName: newProjectName,
      projectId: this.selectedProjectId || null,
      stageId: this.selectedStageId || null,
    });
  }

  // Obtenir le projectId correspondant au nom du projet actuel
  getSelectedProjectId(): string {
    return this.selectedProjectId;
  }

  // ── CRM task picker ──────────────────────────────────────────────────────

  private async loadCrmTasks() {
    try {
      let sub: any;
      const currentUser = await new Promise<any>((resolve) => {
        sub = this.userService.currentUser$.subscribe(u => {
          if (u) { sub?.unsubscribe(); resolve(u); }
        });
      });
      if (!currentUser?.id) return;
      const ids = await this.clientService.getTodosAssignedToUser(currentUser.id);
      if (!ids.length) return;
      const { todos } = await this.clientService.fetchTodosPage({ ids, limit: 100 });
      this.crmTasks = todos;
      this.filteredCrmTasks = todos;
      this.cdr.markForCheck();
    } catch (e) {
      console.error('[DetailModal] Failed to load CRM tasks', e);
    }
  }

  openCrmPicker() {
    this.showCrmPicker = !this.showCrmPicker;
    this.crmPickerSearch = '';
    this.filteredCrmTasks = [...this.crmTasks];
    this.cdr.markForCheck();
  }

  onPickerSearch() {
    const q = this.crmPickerSearch.toLowerCase();
    this.filteredCrmTasks = q
      ? this.crmTasks.filter(t => t.name.toLowerCase().includes(q))
      : [...this.crmTasks];
  }

  selectCrmTask(task: Todo) {
    const newName = `[Task] ${task.name}`;
    this.crmTaskLabel = task.name;
    this.entry.projectName = newName;
    this.showCrmPicker = false;
    this.updateTimeEntry({ projectName: newName });
    this.cdr.markForCheck();
  }

  toggleBillable() {
    const newVal = !(this.entry.billable ?? true);
    this.entry.billable = newVal;
    this.updateTimeEntry({ billable: newVal });
    this.cdr.markForCheck();
  }

  closeModal() {
    this.close.emit();
  }

  deleteTimeEntry() {
    if (this.isLocked) return; // locked entries cannot be deleted by non-admins
    this.closeModal();
    if (this.entry.id) this.timeEntryService.deleteTimeEntry(this.entry.id);
    else console.error(`Failed to delete this time entry no id found `);
  }

  updateTimeEntry(updates: Partial<TimeEntry>) {
    if (this.isLocked) return; // locked entries cannot be updated by non-admins
    if (updates.startTime) {
      const timeStr = updates.startTime as unknown as string;
      const [hours, minutes] = timeStr.split(':').map(Number);
      const date = new Date(this.entry.startTime);
      date.setHours(hours, minutes);
      updates.startTime = date;
    }
    if (updates.endTime) {
      const timeStr = updates.endTime as unknown as string;
      const [hours, minutes] = timeStr.split(':').map(Number);
      const date = new Date(this.entry.endTime);
      date.setHours(hours, minutes);
      updates.endTime = date;
    }

    // S'assurer que projectName est une string
    if (updates.projectName && typeof updates.projectName === 'object') {
      updates.projectName = (updates.projectName as any).projectName || String(updates.projectName);
    }

    if (this.entry.id) {
      this.timeEntryService.updateTimeEntry(this.entry.id, updates)
        .then(() => console.log('✅ Time entry updated'))
        .catch(err => console.error('❌ Update error:', err));
      this.update.emit();
    }
  }

  async toggleImages() {
    if (this.imagesLoaded) {
      this.imageUrls = [];
      this.imagesLoaded = false;
      return;
    }
    if (!this.entry.imagePaths || this.entry.imagePaths.length === 0) return;
    const resolved: { path: string; url: string }[] = [];
    for (const path of this.entry.imagePaths) {
      const url = await this.fileService.getFileUrl(path);
      if (url) resolved.push({ path, url });
    }
    this.imageUrls = resolved;
    this.imagesLoaded = true;
  }


  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
  }

  async addImage() {
    if (!this.entry.id || this.selectedFiles.length === 0) return;
    try {
      const uploadedPaths = await this.fileService.uploadFiles(
        this.selectedFiles,
        'timeEntryImage',
        this.entry.id
      );
      await this.timeEntryService.addImagesToEntry(
        this.entry.id,
        uploadedPaths
      );
      this.entry.imagePaths = [
        ...(this.entry.imagePaths ?? []),
        ...uploadedPaths
      ];
      this.selectedFiles = [];
      if (this.imagesLoaded) {
        await this.toggleImages();
        await this.toggleImages();
      }
    } catch (error) {
      console.error('Failed to add images', error);
    }
  }

  async deleteImage(path: string) {
    if (!this.entry?.id) return;
    try {
      await this.fileService.deleteFile(path);
      await this.timeEntryService.removeImageFromEntry(this.entry.id, path);
      this.entry.imagePaths = (this.entry.imagePaths ?? []).filter(
        p => p !== path
      );
      if (this.imagesLoaded) {
        await this.toggleImages();
        await this.toggleImages();
      }
    } catch (error) {
      console.error('Failed to delete image', error);
    }
  }

  startDrag(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.dragStartX = event.clientX - this.currentLeft;
    this.dragStartY = event.clientY - this.currentTop;

    // Run outside Angular zone to avoid excessive change detection
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
    });
  }

  onDrag = (event: MouseEvent) => {
    if (!this.dragging) return;
    event.preventDefault();
    this.currentLeft = event.clientX - this.dragStartX;
    this.currentTop = event.clientY - this.dragStartY;
    this.cdr.detectChanges(); // Manual change detection for position update
  };

  stopDrag = () => {
    this.dragging = false;
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    this.ngZone.run(() => this.cdr.markForCheck());
  }

}
